import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import "./CSS/LoginRegistration.css";
import { API_BASE_URL, API_ENDPOINTS } from "../constants/apiConstants";
import {
  storeTokens,
  storeUserId,
  isAuthenticated,
  getStoredTokens
} from "../utils/auth";

const LOGO_URL = process.env.PUBLIC_URL + "/adflynLogo.png";

/**
 * Utility functions to reduce code duplication
 */
const makeApiCall = async (endpoint, payload) => {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json"
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  return { response, data };
};

const withLoadingAndError = async (asyncFn, setLoading, onError) => {
  setLoading(true);
  try {
    return await asyncFn();
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      console.error("Operation failed:", error);
      alert(error.message || "Operation failed. Please try again.");
    }
    return { success: false, error };
  } finally {
    setLoading(false);
  }
};

const autoLoginAndNavigate = async (email, password, navigate, successMessage = "Success") => {
  try {
    const loginPayload = { email, password };
    const { response: loginResponse, data: loginData } = await makeApiCall(
      `${API_BASE_URL}${API_ENDPOINTS.LOGIN}`,
      loginPayload
    );

    if (loginResponse.ok && loginData.accessToken) {
      const tokens = {
        accessToken: loginData.accessToken,
        idToken: loginData.idToken,
        refreshToken: loginData.refreshToken
      };

      storeTokens(tokens);
      // Store userId if provided
      if (loginData.userId) {
        storeUserId(loginData.userId);
        console.log('✅ User ID stored:', loginData.userId);
      }
      await checkOnboardingAndNavigate(tokens, navigate);
      return { success: true };
    } else {
      return { success: false, error: loginData.error || "Auto-login failed" };
    }
  } catch (error) {
    return { success: false, error: error.message || "Auto-login failed" };
  }
};

const fetchOnboardingStatus = async (tokens) => {
  const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER_ONBOARDING}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tokens.idToken}`
    }
  });

  if (response.ok) {
    const data = await response.json();
    return data.onboardingComplete || false;
  }

  throw new Error('Failed to fetch onboarding status');
};

const navigateBasedOnOnboarding = (onboardingComplete, navigate) => {
  navigate(onboardingComplete ? '/adflyn-chatbot' : '/webpage-analyzer');
};

const checkOnboardingAndNavigate = async (tokens, navigate) => {
  try {
    const onboardingResponse = await fetch(`${API_BASE_URL}/auth/post-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokens.idToken}`
      }
    });

    const onboardingData = await onboardingResponse.json();
    const onboardingComplete = onboardingData.onboardingComplete || false;

    navigateBasedOnOnboarding(onboardingComplete, navigate);
    return true;
  } catch (onboardingError) {
    console.warn('⚠️ Failed to check onboarding status, redirecting to web analyzer', onboardingError);
    navigate('/webpage-analyzer');
    return false;
  }
};

const handleAuthSuccess = async (tokens, navigate, type = "authentication") => {
  storeTokens(tokens);
  console.log(`✅ ${type} successful, checking onboarding status`);

  try {
    if (tokens?.idToken) {
      const onboardingComplete = await fetchOnboardingStatus(tokens);
      navigateBasedOnOnboarding(onboardingComplete, navigate);
      return;
    }
  } catch (error) {
    console.warn('⚠️ Failed to check onboarding status after login', error);
  }

  navigate('/webpage-analyzer');
};

const handleAuthError = (error, type = "Authentication") => {
  console.error(`❌ ${type} error:`, error);
  let errorMessage = `${type} failed. `;

  if (error.message?.includes("credential") || error.message?.includes("token")) {
    errorMessage += "There was an issue with the authentication token. Please try again.";
  } else if (error.message?.includes("network") || error.message?.includes("fetch")) {
    errorMessage += "Network error occurred. Please check your internet connection and try again.";
  } else if (error.message?.includes("email")) {
    errorMessage += error.message;
  } else {
    errorMessage += error.message || "An unexpected error occurred. Please try again.";
  }

  alert(errorMessage);
};

/**
 * Utility to validate the password based on standard security requirements.
 */
const validatePassword = (password) => {
  if (password.length < 8)
    return "Password must be at least 8 characters long.";
  if (!/[A-Z]/.test(password))
    return "Password must contain at least one uppercase letter.";
  if (!/[a-z]/.test(password))
    return "Password must contain at least one lowercase letter.";
  if (!/\d/.test(password))
    return "Password must contain at least one number.";
  if (!/[^A-Za-z0-9]/.test(password))
    return "Password must contain at least one special character.";
  return null;
};

const PasswordInput = ({ id, value, onChange, placeholder, showToggle, toggleState, onToggle }) => (
  <div className="mb-4 relative">
    <input
      id={id}
      type={showToggle && toggleState ? "text" : "password"}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required
      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12"
    />
    {showToggle && (
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
      >
        👁️
      </button>
    )}
  </div>
);

PasswordInput.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  showToggle: PropTypes.bool,
  toggleState: PropTypes.bool,
  onToggle: PropTypes.func,
};

const ErrorMessage = ({ message }) => message ? (
  <div className="error-message mb-4">{message}</div>
) : null;

ErrorMessage.propTypes = {
  message: PropTypes.string,
};

const TextInput = ({ id, type = "text", value, onChange, placeholder, required = true }) => (
  <div className="mb-4">
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
    />
  </div>
);

TextInput.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  required: PropTypes.bool,
};

const AuthTogglePrompt = ({ message, actionLabel, onClick }) => (
  <div className="text-center mt-6 text-sm text-gray-600">
    {message}{' '}
    <button
      type="button"
      onClick={onClick}
      className="text-purple-600 font-semibold hover:underline bg-transparent border-none p-0 cursor-pointer"
    >
      {actionLabel}
    </button>
  </div>
);

AuthTogglePrompt.propTypes = {
  message: PropTypes.string.isRequired,
  actionLabel: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

// Left Panel Component
const LeftPanel = () => {
  return (
    <div className="left-panel">
      <div className="left-panel-content">
        <img
          src={LOGO_URL}
          alt="Adflyn Logo"
          className="left-panel-logo"
        />
      </div>
    </div>
  );
};

const LoginRegistration = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationPassword, setVerificationPassword] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);

  // Local form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirmPassword, setRegConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [passwordError, setPasswordError] = useState(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState(null);
  const [loginError, setLoginError] = useState("");
  const [registrationError, setRegistrationError] = useState("");
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [resetPasswordError, setResetPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check if user is already authenticated on component mount
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!isAuthenticated()) return;

      try {
        const tokens = getStoredTokens();
        if (tokens?.idToken) {
          const onboardingComplete = await fetchOnboardingStatus(tokens);
          navigateBasedOnOnboarding(onboardingComplete, navigate);
          return;
        }
      } catch (error) {
        console.warn('⚠️ Failed to fetch onboarding status, redirecting to web analyzer', error);
      }

      navigate('/webpage-analyzer');
    };

    checkOnboardingStatus();
  }, [navigate]);

  // --- Google Auth via Cognito with PKCE ---
  const generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
  };

  const base64UrlEncode = (buffer) => {
    const base64 = btoa(String.fromCodePoint(...buffer));
    return base64
      .replaceAll('+', '-')
      .replaceAll('/', '_')
      .replaceAll('=', '');
  };

  const generateCodeChallenge = async (verifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(hash));
  };

  const handleGoogleLogin = async () => {
    console.log("🚀 Redirecting to Cognito for Google authentication with PKCE...");

    const domain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI;
    const scope = "openid email profile";

    // Generate PKCE parameters
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Store code verifier in sessionStorage for callback
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);
    console.log("🔐 PKCE code verifier generated and stored");

    // Build Cognito authorization URL with PKCE
    const authUrl =
      `${domain}/oauth2/authorize` +
      `?identity_provider=Google` +
      `&response_type=code` +
      `&client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(scope)}` +
      `&code_challenge=${codeChallenge}` +
      `&code_challenge_method=S256`;

    console.log("📍 Redirecting to:", authUrl);

    // Redirect to Cognito
    // eslint-disable-next-line no-undef
    globalThis.location.href = authUrl;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");

    await withLoadingAndError(async () => {
      const payload = { email: loginEmail, password: loginPassword };
      const { response, data } = await makeApiCall(`${API_BASE_URL}${API_ENDPOINTS.LOGIN}`, payload);

      if (response.ok && data.accessToken) {
        const tokens = {
          accessToken: data.accessToken,
          idToken: data.idToken,
          refreshToken: data.refreshToken
        };

        // Store userId if provided
        if (data.userId) {
          storeUserId(data.userId);
          console.log('✅ User ID stored:', data.userId);
        }

        await handleAuthSuccess(tokens, navigate, "Email login");
      } else {
        setLoginError(data.error || "Invalid email or password. Please try again.");
      }
    }, setLoading, (error) => {
      setLoginError(error.message || "Login failed. Please check your connection and try again.");
    });
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    setRegistrationError("");

    await withLoadingAndError(async () => {
      // Validation
      const pError = validatePassword(regPassword);
      setPasswordError(pError);
      const cpError = regPassword === regConfirmPassword ? null : "Passwords do not match.";
      setConfirmPasswordError(cpError);

      if (pError || cpError) {
        setRegistrationError(pError || cpError);
        return;
      }

      const payload = { name: regName, email: regEmail, password: regPassword };
      const { response, data } = await makeApiCall(`${API_BASE_URL}${API_ENDPOINTS.SIGNUP}`, payload);

      if (response.ok) {
        console.log("✅ Registration successful, email verification required");
        setVerificationEmail(regEmail);
        setVerificationPassword(regPassword);
        setShowOtpVerification(true);
        setRegPassword("");
        setRegConfirmPassword("");
        setPasswordError(null);
        setConfirmPasswordError(null);
      } else {
        setRegistrationError(data.error || "Registration failed. Please try again.");
      }
    }, setLoading, (error) => {
      setRegistrationError(error.message || "Registration failed. Please check your connection and try again.");
    });
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      alert("Please enter the verification code");
      return;
    }

    await withLoadingAndError(async () => {
      const payload = { email: verificationEmail, code: otp };
      const { response, data } = await makeApiCall(`${API_BASE_URL}${API_ENDPOINTS.VERIFY_EMAIL}`, payload);

      if (response.ok) {
        console.log("✅ Email verified successfully! Auto-logging in...");

        const result = await autoLoginAndNavigate(verificationEmail, verificationPassword, navigate);

        if (!result.success) {
          alert("✅ Email verified! Please login to continue.");
          setShowOtpVerification(false);
          setIsLogin(true);
          setOtp("");
        }

        setVerificationPassword("");
        setVerificationEmail("");
      } else {
        alert(`Verification failed: ${data.error || "Invalid code. Please try again."}`);
      }
    }, setLoading, (error) => handleAuthError(error, "Email verification"));
  };

  const handleResendCode = async () => {
    await withLoadingAndError(async () => {
      const payload = { email: verificationEmail };
      const { response, data } = await makeApiCall(`${API_BASE_URL}${API_ENDPOINTS.RESEND_CODE}`, payload);

      if (response.ok) {
        alert("✅ Verification code resent to your email!");
      } else {
        alert(`Failed to resend code: ${data.error || "Please try again."}`);
      }
    }, setLoading, (error) => {
      console.error("Resend code error:", error);
      alert("Failed to resend code. Please try again.");
    });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!forgotPasswordEmail.trim()) {
      setForgotPasswordError("Please enter your email address");
      return;
    }

    setForgotPasswordError("");

    await withLoadingAndError(async () => {
      const payload = { email: forgotPasswordEmail };
      const { response, data } = await makeApiCall(`${API_BASE_URL}${API_ENDPOINTS.FORGOT_PASSWORD}`, payload);

      if (response.ok) {
        alert("✅ Password reset code sent to your email!");
        setShowResetForm(true);
      } else {
        setForgotPasswordError(data.error || "Failed to send reset code. Please try again.");
      }
    }, setLoading, (error) => {
      console.error("Forgot password error:", error);
      setForgotPasswordError("Failed to send reset code. Please check your connection and try again.");
    });
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!resetCode.trim() || !newPassword.trim() || !confirmNewPassword.trim()) {
      setResetPasswordError("Please fill in all fields");
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (passwordValidation) {
      setResetPasswordError(passwordValidation);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setResetPasswordError("Passwords do not match");
      return;
    }

    setResetPasswordError("");

    await withLoadingAndError(async () => {
      const payload = {
        email: forgotPasswordEmail,
        code: resetCode,
        newPassword: newPassword
      };
      const { response, data } = await makeApiCall(`${API_BASE_URL}${API_ENDPOINTS.RESET_PASSWORD}`, payload);

      if (response.ok) {
        console.log("✅ Password reset successfully! Auto-logging in...");

        const result = await autoLoginAndNavigate(forgotPasswordEmail, newPassword, navigate);

        if (!result.success) {
          alert("✅ Password reset successfully! Please login with your new password.");
          setShowForgotPassword(false);
          setShowResetForm(false);
          setForgotPasswordEmail('');
          setResetCode('');
          setNewPassword('');
          setConfirmNewPassword('');
          setIsLogin(true);
        }
      } else {
        setResetPasswordError(data.error || "Invalid code or request. Please try again.");
      }
    }, setLoading, (error) => {
      console.error("Reset password error:", error);
      setResetPasswordError("Failed to reset password. Please check your connection and try again.");
    });
  };

  const validatePasswordMatch = useCallback((password, confirmPassword) => {
    return password === confirmPassword ? null : "Passwords do not match.";
  }, []);

  const handlePasswordChange = useCallback(
    (e) => {
      const newPassword = e.target.value;
      setRegPassword(newPassword);
      setPasswordError(validatePassword(newPassword));

      if (regConfirmPassword) {
        setConfirmPasswordError(validatePasswordMatch(newPassword, regConfirmPassword));
      }
    },
    [regConfirmPassword, validatePasswordMatch]
  );

  const handleConfirmPasswordChange = useCallback(
    (e) => {
      const newConfirmPassword = e.target.value;
      setRegConfirmPassword(newConfirmPassword);
      setConfirmPasswordError(validatePasswordMatch(regPassword, newConfirmPassword));
    },
    [regPassword, validatePasswordMatch]
  );

  return (
    <div className="auth-container">
      <LeftPanel />

      <div className="right-panel">
        <div className="auth-form-container">
          {/* OTP Verification Form */}
          {showOtpVerification ? (
            <div className="otp-verification-section">
              <h2 className="form-title">Verify Your Email</h2>
              <div className="info-message">
                <p>We've sent a verification code to <strong>{verificationEmail}</strong></p>
              </div>

              <form onSubmit={handleVerifyOtp}>
                <div className="mb-6">
                  <input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter 6-digit code"
                    maxLength="6"
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-2xl tracking-widest"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="submit-btn"
                >
                  {loading ? "Verifying..." : "Verify Email"}
                </button>

                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={loading}
                  className="secondary-btn mt-3"
                >
                  Resend Code
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowOtpVerification(false);
                    setOtp("");
                    setIsLogin(true);
                  }}
                  className="text-center w-full mt-4 text-sm text-gray-600 hover:text-gray-800"
                >
                  Back to Login
                </button>
              </form>
            </div>
          ) : (() => {
            if (showForgotPassword) {
              return (
                /* Forgot Password Flow */
                <div className="forgot-password-section">
                  {(() => {
                    if (showResetForm) {
                      return (
                        /* Enter Reset Code and New Password */
                        <div>
                          <h2 className="form-title">Reset Password</h2>
                          <div className="info-message mb-6">
                            <p>📧 Code sent to <strong>{forgotPasswordEmail}</strong></p>
                          </div>

                          {resetPasswordError && <ErrorMessage message={resetPasswordError} />}

                          <form onSubmit={handleResetPassword}>
                            <TextInput
                              id="reset-code"
                              value={resetCode}
                              onChange={(e) => {
                                setResetCode(e.target.value);
                                setResetPasswordError("");
                              }}
                              placeholder="Code"
                            />

                            <PasswordInput
                              id="new-password"
                              value={newPassword}
                              onChange={(e) => {
                                setNewPassword(e.target.value);
                                setResetPasswordError("");
                              }}
                              placeholder="New Password"
                              showToggle={true}
                              toggleState={showPassword}
                              onToggle={() => setShowPassword(!showPassword)}
                            />

                            <PasswordInput
                              id="confirm-new-password"
                              value={confirmNewPassword}
                              onChange={(e) => {
                                setConfirmNewPassword(e.target.value);
                                setResetPasswordError("");
                              }}
                              placeholder="Confirm Password"
                              showToggle={true}
                              toggleState={showConfirmPassword}
                              onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                            />

                            <button
                              type="submit"
                              disabled={loading}
                              className="submit-btn"
                            >
                              {loading ? "Resetting..." : "Submit"}
                            </button>
                          </form>
                        </div>
                      );
                    }
                    return (
                      /* Request Reset Code */
                      <div>
                        <h2 className="form-title">Reset Password</h2>
                        <p className="form-subtitle">
                          Enter your username and we'll send you a verification code.
                        </p>

                        {forgotPasswordError && <ErrorMessage message={forgotPasswordError} />}

                        <form onSubmit={handleForgotPassword}>
                          <TextInput
                            id="forgot-email"
                            type="email"
                            value={forgotPasswordEmail}
                            onChange={(e) => {
                              setForgotPasswordEmail(e.target.value);
                              setForgotPasswordError("");
                            }}
                            placeholder="Enter your username"
                          />

                          <button
                            type="submit"
                            disabled={loading}
                            className="submit-btn"
                          >
                            {loading ? "Sending..." : "Send code"}
                          </button>
                        </form>
                      </div>
                    );
                  })()}
                </div>
              );
            }
            return (
              <>
                {/* Main Login/Registration Form */}
                <div className="form-header">
                  <h2 className="form-title">
                    {isLogin ? "Your Dedicated AI Social Media Advertising Specialist" : "Get Started Now"}
                  </h2>
                  {isLogin && (
                    <p className="form-subtitle">
                      Don't have an account? <button type="button" className="text-purple-600 cursor-pointer hover:underline bg-transparent border-none p-0" onClick={() => setIsLogin(false)}>Sign up</button>
                    </p>
                  )}
                </div>

                {/* OAuth Buttons */}
                <div className="oauth-buttons">
                  <button
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    className="oauth-btn"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none">
                      <path d="M19.6 10.227c0-.709-.064-1.39-.182-2.045H10v3.868h5.382a4.6 4.6 0 01-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.35z" fill="#4285F4" />
                      <path d="M10 20c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H1.064v2.59A9.996 9.996 0 0010 20z" fill="#34A853" />
                      <path d="M4.405 11.9c-.2-.6-.314-1.24-.314-1.9 0-.66.114-1.3.314-1.9V5.51H1.064A9.996 9.996 0 000 10c0 1.614.386 3.14 1.064 4.49l3.34-2.59z" fill="#FBBC05" />
                      <path d="M10 3.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C14.959.99 12.695 0 10 0 6.09 0 2.71 2.24 1.064 5.51l3.34 2.59C5.19 5.736 7.395 3.977 10 3.977z" fill="#EA4335" />
                    </svg>
                    <span>Sign In with Google</span>
                  </button>
                </div>

                {/* Divider */}
                <div className="divider">
                  <span>OR</span>
                </div>

                {/* Error Messages */}
                {isLogin && <ErrorMessage message={loginError} />}
                {!isLogin && <ErrorMessage message={registrationError} />}

                {/* Email/Password Form */}
                <form onSubmit={isLogin ? handleLoginSubmit : handleRegistrationSubmit}>
                  {!isLogin && (
                    <TextInput
                      id="reg-name"
                      value={regName}
                      onChange={(e) => {
                        setRegName(e.target.value);
                        setRegistrationError("");
                      }}
                      placeholder="Enter your name"
                    />
                  )}

                  <TextInput
                    id={isLogin ? "login-email" : "reg-email"}
                    type="email"
                    value={isLogin ? loginEmail : regEmail}
                    onChange={(e) => {
                      if (isLogin) {
                        setLoginEmail(e.target.value);
                        setLoginError("");
                      } else {
                        setRegEmail(e.target.value);
                        setRegistrationError("");
                      }
                    }}
                    placeholder="Enter your email"
                  />

                  <PasswordInput
                    id={isLogin ? "login-password" : "reg-password"}
                    value={isLogin ? loginPassword : regPassword}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (isLogin) {
                        setLoginPassword(newValue);
                        setLoginError("");
                      } else {
                        handlePasswordChange(e);
                        setRegistrationError("");
                      }
                    }}
                    placeholder="Enter your Password"
                    showToggle={true}
                    toggleState={showPassword}
                    onToggle={() => setShowPassword(!showPassword)}
                  />

                  {!isLogin && (
                    <>
                      <PasswordInput
                        id="reg-confirm-password"
                        value={regConfirmPassword}
                        onChange={(e) => {
                          handleConfirmPasswordChange(e);
                          setRegistrationError("");
                        }}
                        placeholder="Confirm your password"
                        showToggle={true}
                        toggleState={showConfirmPassword}
                        onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                      />
                      {(passwordError || confirmPasswordError) && (
                        <div className="text-xs text-red-500 mb-4">
                          {passwordError || confirmPasswordError}
                        </div>
                      )}
                    </>
                  )}

                  {isLogin && (
                    <div className="text-right mb-6">
                      <button
                        type="button"
                        className="text-sm text-purple-600 hover:underline bg-transparent border-none p-0 cursor-pointer"
                        onClick={() => {
                          setShowForgotPassword(true);
                          setForgotPasswordEmail(loginEmail);
                        }}
                      >
                        Forgot your password?
                      </button>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="submit-btn"
                  >
                    {(() => {
                      if (loading) return "Please wait...";
                      return isLogin ? "Sign in" : "Sign up";
                    })()}
                  </button>
                </form>

                {/* Toggle Login/Register */}
                {isLogin && (
                  <AuthTogglePrompt
                    message="Don't have an account?"
                    actionLabel="Sign up"
                    onClick={() => setIsLogin(false)}
                  />
                )}

                {!isLogin && (
                  <AuthTogglePrompt
                    message="Already have an account?"
                    actionLabel="Sign in"
                    onClick={() => setIsLogin(true)}
                  />
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default LoginRegistration;
